import JobRoleService from 'signup-ui-job-role/services/JobRoleService';
import { Data } from '../../signup-data/DATA';
import { getCompanySizeFormatted } from '../../signup-data/getCompanySizeFormatted';
import { getDataKeysByStep } from '../../step/getStepData';
import trimEmptyValuesFromObject from '../../utils/trimEmptyValuesFromObject';
import { Step } from '../../views/Step';
import { getPostAccountCreationSteps, getPostExistingAccountSelectionSteps, getPreVerificationSteps } from '../flow/selectors';
import { DataStateKey, SeniorityLevel } from './types';
import { getIsActiveAndAssignedToVariant } from '../experiments/selectors';
import { ExperimentKeys } from '../../experiments/ExperimentKeys';
import { ExperimentParameterGroup } from '../../experiments/ExperimentParameterGroup';
import { ACQ134Industry, Industry } from '../../signup-data/IndusrtyOption';
export const getSignupData = state => state.signupData;
export const getSignupDataEntries = state => {
  return Object.entries(getSignupData(state));
};
export const getSignupDataEntry = (state, dataPoint) => {
  return getSignupData(state)[dataPoint];
};
export const getSignupDataValue = (state, dataKey) => {
  return getSignupDataEntry(state, dataKey)[DataStateKey.value];
};
export const getSignupDataPrefilled = (state, dataKey) => getSignupDataEntry(state, dataKey)[DataStateKey.prefilled];
export const getSignupDataValidation = (state, dataKey) => getSignupDataEntry(state, dataKey)[DataStateKey.validation];
export const getSignupDataErrorMessage = (state, dataKey) => getSignupDataEntry(state, dataKey)[DataStateKey.errorMessage];
export const getSignupDataSkipped = (state, dataKey) => getSignupDataEntry(state, dataKey)[DataStateKey.skipped];
export const getSignupDataEntriesForDataKeys = (state, dataPoints) => dataPoints.map(dataPoint => getSignupDataEntry(state, dataPoint));
export const getSignupDataValues = (state, dataPoints) => dataPoints.map(dataPoint => getSignupDataValue(state, dataPoint));
export const getSignupDataValidations = (state, dataPoints) => dataPoints.map(dataPoint => getSignupDataValidation(state, dataPoint));
export const getSignupDataErrorMessages = (state, dataPoints) => dataPoints.map(dataPoint => getSignupDataErrorMessage(state, dataPoint));
export const getFlatSignupDataValueMap = state => {
  return getSignupDataEntries(state).reduce((acc, [dataPoint, {
    value
  }]) => {
    acc[dataPoint] = value;
    return acc;
  }, {});
};
export const getIndustry = state => {
  const industry = getSignupDataValue(state, Data.Industry);

  if (getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0134, [ExperimentParameterGroup.Variant_A, ExperimentParameterGroup.Variant_B]) && industry === ACQ134Industry.TechnologyAndServices) {
    return Industry.TechnologyAndServices;
  }

  return industry;
};
export const getOptionalPhoneNumber = state => getSignupDataValue(state, Data.OptionalPhoneNumber) || getSignupDataValue(state, Data.PhoneNumber) || '';
export const signupDataAreFilled = (state, dataPoints) => {
  if (dataPoints.length === 0) {
    return true;
  }

  return getSignupDataEntriesForDataKeys(state, dataPoints).filter(data => !data[DataStateKey.skipped]).every(data => data && Boolean(data[DataStateKey.value]));
};

const signupDataAreCompleted = (state, dataPoints) => {
  if (dataPoints.length === 0) {
    return true;
  }

  return getSignupDataEntriesForDataKeys(state, dataPoints).filter(data => !data[DataStateKey.skipped]).every(data => {
    return data && Boolean(data[DataStateKey.value]) && data[DataStateKey.completed];
  });
};

export const isSignupDataCompleted = (state, datapoint) => {
  const data = getSignupDataEntry(state, datapoint);
  return data && Boolean(data[DataStateKey.value]) && data[DataStateKey.completed];
};
export const getFilledSignupData = state => {
  const dataKeys = Object.values(Data);
  return dataKeys.reduce((filledData, dataPoint) => {
    const value = getSignupDataValue(state, dataPoint);

    if (value) {
      filledData[dataPoint] = value;
    }

    return filledData;
  }, {});
};
export const getPreAccountCreationSurveyData = state => {
  const {
    companySizeNumber,
    companySizeLabel
  } = getCompanySizeFormatted(getSignupDataValue(state, Data.CompanySize));
  const managementLevel = getSignupDataValue(state, Data.ManagementLevel);
  const seniority = SeniorityLevel[managementLevel];
  return Object.assign({}, companySizeLabel && {
    companySize: companySizeLabel
  }, {
    jobRole: JobRoleService.getCategory({
      id: getSignupDataValue(state, Data.JobRole)
    }),
    jobRoleDetailed: getSignupDataValue(state, Data.JobRole)
  }, companySizeNumber && {
    companySizeNumber
  }, {
    industry: getIndustry(state)
  }, seniority && {
    jobTitle: seniority
  });
};
export const getAccountCreatedFormSurveyData = state => {
  const {
    companySizeNumber
  } = getCompanySizeFormatted(getSignupDataValue(state, Data.CompanySize));
  const optionalPhoneNumber = getOptionalPhoneNumber(state);
  const jobRole = getSignupDataValue(state, Data.JobRole);
  const crmExperience = getSignupDataValue(state, Data.CrmExperienceLevel);
  const hubInterest = getSignupDataValue(state, Data.HubInterest);
  const managementLevel = getSignupDataValue(state, Data.ManagementLevel);
  const seniority = SeniorityLevel[managementLevel];
  return trimEmptyValuesFromObject(Object.assign({
    companySize: companySizeNumber,
    jobRoleDetailed: jobRole,
    industry: getIndustry(state)
  }, jobRole && {
    salesSignupJobRole: JobRoleService.getCategory({
      id: jobRole
    })
  }, {
    jobField: getSignupDataValue(state, Data.JobField)
  }, optionalPhoneNumber && {
    phone: optionalPhoneNumber
  }, {}, crmExperience && {
    crmExperience
  }, {}, hubInterest && {
    hubInterest
  }, {}, seniority && {
    jobTitle: seniority
  }));
};
export const getSurveyData = state => {
  const [crmExperience, jobField, hubInterest, cmsSignupIntent, cmsBusinessMaturity] = getSignupDataValues(state, [Data.CrmExperienceLevel, Data.JobField, Data.HubInterest, Data.CmsSignupIntent, Data.CmsBusinessMaturity]);
  return Object.assign({
    crmExperience,
    jobField
  }, hubInterest && {
    hubInterest
  }, {}, cmsSignupIntent && {
    purpose: cmsSignupIntent
  }, {}, cmsBusinessMaturity && {
    stage: cmsBusinessMaturity
  }, {}, getPreAccountCreationSurveyData(state));
};
export const getSurveyDataForRedirect = state => {
  const surveyData = getSurveyData(state);
  const companySize = {
    companySizeLabel: surveyData.companySize,
    companySizeNumber: surveyData.companySizeNumber
  };
  const cmsSignupIntent = surveyData.purpose; // We do not send the job field to the redirect library if
  // we are on a flow where we ask the hubInterest.

  if (surveyData.hubInterest) {
    delete surveyData.jobField;
  }

  delete surveyData.phoneNumberSubmitted;
  delete surveyData.companySizeNumber;
  delete surveyData.purpose;
  delete surveyData.stage;
  return Object.assign({}, surveyData, {
    companySize,
    cmsSignupIntent
  });
};
export const hasAnsweredAllPostAccountCreatedQuestions = state => {
  let postAccountCreation = getPostAccountCreationSteps(state);

  if (postAccountCreation.includes(Step.HubInterest) && !postAccountCreation.includes(Step.JobField)) {
    postAccountCreation = [...postAccountCreation, Step.JobField];
  }

  const stepData = getDataKeysByStep(postAccountCreation);
  const allCompleted = signupDataAreCompleted(state, stepData);
  return allCompleted;
};
export const hasAnsweredAllPostExistingAccountSelectedQuestions = state => {
  const postExistingAccountSelection = getPostExistingAccountSelectionSteps(state);

  if (postExistingAccountSelection.length === 0) {
    return false;
  }

  const stepData = getDataKeysByStep(postExistingAccountSelection);
  const allCompleted = signupDataAreCompleted(state, stepData);
  return allCompleted;
};
export const isVerificationStep = stepKey => {
  return stepKey === Step.Verification || stepKey === Step.CodeVerification;
};
export const getPreVerificationDataKeys = state => {
  const preVerification = getPreVerificationSteps(state);
  const steps = preVerification.filter(step => !isVerificationStep(step));
  return getDataKeysByStep(steps);
};
export const getCountry = state => getSignupDataValue(state, Data.Country) || '';
export const isAsyncValidationResultUpToDate = (state, dataKey, validatedValue) => {
  const signupDataItem = getSignupDataEntry(state, dataKey);
  return signupDataItem && signupDataItem.value === validatedValue;
};